import { useMemo, useState } from "react";

import flatMapDeep from "lodash/flatMapDeep";
import { useNavigate } from "react-router";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { useAuth } from "@/components/lib/auth";
import { assignmentsUniqByCoach } from "@/components/pieces/coaching/CoachingDashboard";
import { MeetingBasics } from "@/components/pieces/coaching/meetings/MeetingBasics";
import {
  buildOverlapMeetingList,
  getMeetingStatus,
  getUserBufferSetting,
} from "@/components/pieces/coaching/meetings/utils";
import { getUser } from "@/components/session/JwtTokenManager";
import { SequentialRevealList } from "@/components/ui/SequentialRevealList";
import {
  AssignmentType,
  MeetingStatus,
  MeetingType,
  ProgramComponentFeed,
  ProgramComponentType,
  ProgramMiniType,
  ProgramType,
  useAssignmentsLazyQuery,
  useProgramFeedQuery,
  useSessionsQuery,
} from "@/graphql";
import { Assignment } from "@/types/coaches";

import { CoachingBlock } from "./CoachingBlock";
import { LeadProgramBlock } from "./LeadProgramBlock";
import { ParticipantProgramBlock } from "./ParticipantProgramBlock";
import { RoleplayBlock } from "./RoleplayBlock";

export function HomeDashboard() {
  const navigate = useNavigate();
  const isAdmin = isWorkspaceAdmin();
  const { currentWorkspace } = useAuth();

  const { data: programFeedData, loading: programFeedLoading } =
    useProgramFeedQuery({
      fetchPolicy: "network-only",
      variables: {
        leadProgramSize: 4,
        participantProgramSize: 10,
        userId: getUser()?.id,
      },
      skip: !currentWorkspace.programsEnabled,
    });
  const leadPrograms = currentWorkspace.programsEnabled
    ? (programFeedData?.programFeed?.leadPrograms as
        | ProgramMiniType[]
        | undefined)
    : [];
  const participantPrograms = currentWorkspace.programsEnabled
    ? (programFeedData?.programFeed?.participantPrograms as
        | ProgramMiniType[]
        | undefined)
    : [];
  const participantComponentFeed = currentWorkspace.programsEnabled
    ? (programFeedData?.programFeed?.participantComponentFeed as
        | ProgramComponentFeed[]
        | undefined)
    : [];

  const [upcomingMeetings, setUpcomingMeetings] = useState<MeetingType[]>();
  const [assignments, setAssignments] = useState<AssignmentType[]>();
  const { data: userData, loading: sessionsLoading } = useSessionsQuery({
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      const meetings = (data.currentUser?.meetings || []) as MeetingType[];

      const upcoming = meetings.reduce((result: MeetingType[], meeting) => {
        const status = getMeetingStatus(meeting);
        if (status === MeetingStatus.Upcoming) {
          result.push({ ...meeting });
        }
        return result;
      }, []);

      if (!upcoming.length) {
        setUpcomingMeetings([]);
        fetchAssignments();
      }

      const { beforeBuffer, afterBuffer } = getUserBufferSetting(
        data.currentUser!,
      );
      const upcomingMeetings = buildOverlapMeetingList({
        meetingLists: upcoming,
        beforeBuffer,
        afterBuffer,
      });

      setUpcomingMeetings(upcomingMeetings);
    },
  });
  const [fetchAssignments, { loading: assignmentsLoading }] =
    useAssignmentsLazyQuery({
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        const assignments = data.assignments
          ? assignmentsUniqByCoach(data.assignments as Assignment[], undefined)
          : undefined;

        setAssignments(assignments as AssignmentType[]);
      },
    });
  const availableCredits = Number(userData?.currentUser?.availableCredits);
  const isCoachingAvailable =
    (upcomingMeetings && upcomingMeetings?.length > 0) ||
    (assignments && assignments?.length > 0) ||
    availableCredits > 0;

  const isProgramBlockLoading = programFeedLoading;
  const isCoachingBlockLoading =
    sessionsLoading ||
    assignmentsLoading ||
    (!upcomingMeetings?.length && assignments === undefined);

  const liveCoachingMeeting = flatMapDeep(
    participantComponentFeed,
    (group) =>
      group.components
        .filter((component) => !!component?.meetings?.length)
        .map((component) => component?.meetings!) as MeetingType[][],
  )?.find((meeting) =>
    [MeetingStatus.Ongoing, MeetingStatus.Soon].includes(
      meeting.status as MeetingStatus,
    ),
  );

  const showRoleplays = isAdmin || currentWorkspace.roleplaysEnabled;

  const isLoading = isProgramBlockLoading || isCoachingBlockLoading;
  const isInitializing =
    isLoading ||
    !leadPrograms ||
    !upcomingMeetings ||
    (upcomingMeetings.length === 0 && !assignments);

  const [contentfulFeed, emptyFeed] = useMemo(
    () =>
      participantComponentFeed?.reduce(
        ([contentful, empty], feed) => {
          if (feed.components.length > 0) {
            contentful.push(feed);
          } else {
            empty.push(feed);
          }

          return [contentful, empty];
        },
        [[] as ProgramComponentFeed[], [] as ProgramComponentFeed[]],
      ) ?? [[], []],
    [participantComponentFeed],
  );

  const sortedParticipantComponentFeed = [...contentfulFeed, ...emptyFeed];

  return !isInitializing ? (
    <SequentialRevealList className="xs:gap-5 flex flex-col md:gap-6">
      {liveCoachingMeeting ? (
        <div className="round-corners xs:p-5 w-full max-w-150 self-center border-1 border-neutral-300 bg-white hover:bg-blue-50 md:p-6">
          <MeetingBasics
            meeting={liveCoachingMeeting}
            chatChannelId={liveCoachingMeeting?.chatChannelId}
            large={false}
            showMessageButton={true}
            customerId={String(liveCoachingMeeting?.customerId)}
          />
        </div>
      ) : null}
      {participantPrograms?.length
        ? sortedParticipantComponentFeed?.map((feed) => (
            <ParticipantProgramBlock
              key={feed.programId}
              program={
                participantPrograms?.find(
                  (program) => program.id === feed.programId,
                ) as ProgramType
              }
              components={feed.components as ProgramComponentType[]}
              onClick={() => {
                navigate(`/programs/${feed.programId}`);
              }}
            />
          ))
        : null}
      {leadPrograms?.length ? (
        <LeadProgramBlock programs={leadPrograms} />
      ) : null}
      {isCoachingAvailable ? (
        <CoachingBlock
          assignments={assignments}
          upcomingMeetings={upcomingMeetings}
          availableCredits={availableCredits}
        />
      ) : null}
      {!leadPrograms?.length ? <LeadProgramBlock /> : null}
      {!isCoachingAvailable && showRoleplays ? <RoleplayBlock /> : null}
      {!isCoachingAvailable ? <CoachingBlock /> : null}
      {isCoachingAvailable && showRoleplays ? <RoleplayBlock /> : null}
    </SequentialRevealList>
  ) : null;
}
